import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Stack,
  CircularProgress,
  Pagination,
  Card,
  CardContent,
  Tooltip,
  Menu,
} from '@mui/material';
import {
  Refresh,
  Send,
  Visibility,
  MoreVert,
  Warning,
  CheckCircle,
  Cancel,
  Pending,
  Search,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import DOMPurify from 'dompurify';
import { emailLogsApi } from '../services/api';

interface EmailLog {
  id: number;
  to_email: string;
  from_email: string;
  subject: string;
  email_type: string;
  html_content?: string;
  status: 'pending' | 'sent' | 'failed' | 'disabled';
  provider: string;
  provider_message_id?: string;
  error_message?: string;
  retry_count: number;
  metadata?: any;
  user_id?: number;
  user_first_name?: string;
  user_last_name?: string;
  sent_at?: string;
  failed_at?: string;
  created_at: string;
  updated_at: string;
}

interface EmailStats {
  overall: {
    total: number;
    sent: number;
    failed: number;
    disabled: number;
    pending: number;
    success_rate: string;
    period_days: number;
  };
  by_type: Array<{
    email_type: string;
    count: number;
    sent_count: number;
    failed_count: number;
    success_rate: string;
  }>;
  daily: Array<{
    date: string;
    total: number;
    sent: number;
    failed: number;
  }>;
}

interface EmailLogsProps {
  refreshTrigger?: number;
}

const EmailLogs: React.FC<EmailLogsProps> = ({ refreshTrigger = 0 }) => {
  const [logs, setLogs] = useState<EmailLog[]>([]);
  const [stats, setStats] = useState<EmailStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Pagination and filtering
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    status: '',
    email_type: '',
    search: '',
    days: 30
  });
  
  // Dialog states
  const [selectedLog, setSelectedLog] = useState<EmailLog | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [testEmailDialogOpen, setTestEmailDialogOpen] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testType, setTestType] = useState('general');
  
  // Menu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuLog, setMenuLog] = useState<EmailLog | null>(null);

  // Fetch email logs
  const fetchLogs = async () => {
    try {
      setLoading(true);
      const response = await emailLogsApi.getAll({
        page,
        limit: 25,
        ...(filters.status && { status: filters.status }),
        ...(filters.email_type && { email_type: filters.email_type }),
        ...(filters.search && { search: filters.search })
      });

      setLogs(response.data.logs);
      setTotalPages(response.data.pagination.pages);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to fetch email logs');
    } finally {
      setLoading(false);
    }
  };

  // Fetch email statistics
  const fetchStats = async () => {
    try {
      const response = await emailLogsApi.getStats(filters.days);
      setStats(response.data);
    } catch (err: any) {
      console.error('Failed to fetch stats:', err);
    }
  };

  useEffect(() => {
    fetchLogs();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filters, refreshTrigger]);

  // Get status color and icon
  const getStatusDisplay = (status: string) => {
    switch (status) {
      case 'sent':
        return { 
          color: 'success' as const, 
          icon: <CheckCircle fontSize="small" />, 
          label: 'Sent' 
        };
      case 'failed':
        return { 
          color: 'error' as const, 
          icon: <Cancel fontSize="small" />, 
          label: 'Failed' 
        };
      case 'disabled':
        return { 
          color: 'warning' as const, 
          icon: <Warning fontSize="small" />, 
          label: 'Disabled' 
        };
      default:
        return { 
          color: 'default' as const, 
          icon: <Pending fontSize="small" />, 
          label: 'Pending' 
        };
    }
  };

  // Handle resend email
  const handleResendEmail = async (logId: number) => {
    try {
      await emailLogsApi.resend(logId);
      setSuccess('Email resent successfully');
      fetchLogs();
      setAnchorEl(null);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to resend email');
    }
  };

  // Handle send test email
  const handleSendTestEmail = async () => {
    if (!testEmail) return;

    try {
      await emailLogsApi.sendTest({
        to_email: testEmail,
        test_type: testType
      });

      setSuccess('Test email sent successfully');
      setTestEmailDialogOpen(false);
      setTestEmail('');
      fetchLogs();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to send test email');
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Statistics Cards */}
      {stats && (
        <Box 
          sx={{ 
            display: 'grid',
            gridTemplateColumns: { 
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(4, 1fr)'
            },
            gap: 2,
            mb: 3
          }}
        >
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Emails
              </Typography>
              <Typography variant="h4">
                {stats.overall.total}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Last {stats.overall.period_days} days
              </Typography>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Success Rate
              </Typography>
              <Typography variant="h4" color="success.main">
                {stats.overall.success_rate}%
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {stats.overall.sent} sent
              </Typography>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Failed
              </Typography>
              <Typography variant="h4" color="error.main">
                {stats.overall.failed}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Delivery failures
              </Typography>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Disabled
              </Typography>
              <Typography variant="h4" color="warning.main">
                {stats.overall.disabled}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Service disabled
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Controls */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <TextField
            size="small"
            placeholder="Search emails..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            InputProps={{
              startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />
          
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              label="Status"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="sent">Sent</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
              <MenuItem value="disabled">Disabled</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Email Type</InputLabel>
            <Select
              value={filters.email_type}
              onChange={(e) => setFilters({ ...filters, email_type: e.target.value })}
              label="Email Type"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="welcome">Welcome</MenuItem>
              <MenuItem value="password_reset">Password Reset</MenuItem>
              <MenuItem value="workflow_assignment">Workflow Assignment</MenuItem>
              <MenuItem value="workflow_completed">Workflow Completed</MenuItem>
              <MenuItem value="test_email">Test Email</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => {
              fetchLogs();
              fetchStats();
            }}
          >
            Refresh
          </Button>

          <Button
            variant="contained"
            startIcon={<Send />}
            onClick={() => setTestEmailDialogOpen(true)}
          >
            Send Test Email
          </Button>
        </Stack>
      </Paper>

      {/* Email Logs Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Status</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>To</TableCell>
              <TableCell>Subject</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Retries</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No email logs found
                </TableCell>
              </TableRow>
            ) : (
              logs.map((log) => {
                const statusDisplay = getStatusDisplay(log.status);
                return (
                  <TableRow key={log.id}>
                    <TableCell>
                      <Chip
                        icon={statusDisplay.icon}
                        label={statusDisplay.label}
                        color={statusDisplay.color}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={log.email_type.replace('_', ' ')}
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{log.to_email}</TableCell>
                    <TableCell>
                      <Tooltip title={log.subject}>
                        <span>
                          {log.subject.length > 40 
                            ? `${log.subject.substring(0, 40)}...` 
                            : log.subject}
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={new Date(log.created_at).toLocaleString()}>
                        <span>
                          {formatDistanceToNow(new Date(log.created_at), { addSuffix: true })}
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      {log.retry_count > 0 && (
                        <Chip
                          label={`${log.retry_count} retries`}
                          size="small"
                          color="warning"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedLog(log);
                            setViewDialogOpen(true);
                          }}
                        >
                          <Visibility />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            setAnchorEl(e.currentTarget);
                            setMenuLog(log);
                          }}
                        >
                          <MoreVert />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, newPage) => setPage(newPage)}
          />
        </Box>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem
          onClick={() => {
            if (menuLog) {
              setSelectedLog(menuLog);
              setViewDialogOpen(true);
            }
            setAnchorEl(null);
          }}
        >
          <Visibility sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        {menuLog?.status === 'failed' && (
          <MenuItem
            onClick={() => {
              if (menuLog) {
                handleResendEmail(menuLog.id);
              }
            }}
          >
            <Send sx={{ mr: 1 }} />
            Resend Email
          </MenuItem>
        )}
      </Menu>

      {/* View Email Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Email Details
        </DialogTitle>
        <DialogContent>
          {selectedLog && (
            <Stack spacing={3}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box>
                  <Typography variant="subtitle2">Status</Typography>
                  <Chip
                    {...getStatusDisplay(selectedLog.status)}
                    size="small"
                  />
                </Box>
                <Box>
                  <Typography variant="subtitle2">Type</Typography>
                  <Typography>{selectedLog.email_type}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2">To</Typography>
                  <Typography>{selectedLog.to_email}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2">From</Typography>
                  <Typography>{selectedLog.from_email}</Typography>
                </Box>
              </Box>
              
              <Box>
                <Typography variant="subtitle2">Subject</Typography>
                <Typography>{selectedLog.subject}</Typography>
              </Box>
              
              {selectedLog.error_message && (
                <Box>
                  <Typography variant="subtitle2" color="error">
                    Error Message
                  </Typography>
                  <Typography color="error">{selectedLog.error_message}</Typography>
                </Box>
              )}
              
              {selectedLog.metadata && (
                <Box>
                  <Typography variant="subtitle2">Metadata</Typography>
                  <pre style={{ fontSize: '12px', overflow: 'auto' }}>
                    {JSON.stringify(selectedLog.metadata, null, 2)}
                  </pre>
                </Box>
              )}
              
              <Box>
                <Typography variant="subtitle2">Email Content</Typography>
                <Paper sx={{ p: 2, maxHeight: 300, overflow: 'auto' }}>
                  <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedLog.html_content || 'No content available') }} />
                </Paper>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Test Email Dialog */}
      <Dialog
        open={testEmailDialogOpen}
        onClose={() => setTestEmailDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Send Test Email</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="test@example.com"
            />
            <FormControl fullWidth>
              <InputLabel>Test Type</InputLabel>
              <Select
                value={testType}
                onChange={(e) => setTestType(e.target.value)}
                label="Test Type"
              >
                <MenuItem value="general">General Test</MenuItem>
                <MenuItem value="smtp">SMTP Configuration</MenuItem>
                <MenuItem value="template">Template Test</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestEmailDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSendTestEmail}
            variant="contained"
            disabled={!testEmail}
          >
            Send Test Email
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmailLogs;